// ============================================================================
// Reusable "Route to…" affordance (Task 1). Drop on any record detail to hand
// it to a person with a note. Funnels through route_record (RLS-safe).
// ============================================================================
import { useState } from 'react';
import { Send, X } from 'lucide-react';
import { Button, useToast } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { routeRecord, useRoutablePeople, type RoutingEntityType } from './hooks/use-routing-data';
import { errorMessage } from '../../shared/errors';

interface RouteToPanelProps {
  entityType: RoutingEntityType;
  entityId: string;
  onRouted?: () => void;
}

export function RouteToPanel({ entityType, entityId, onRouted }: RouteToPanelProps) {
  const toast = useToast();
  const { employee } = useAuth();
  const { people, isLoading: peopleLoading } = useRoutablePeople();
  const [open, setOpen] = useState(false);
  const [toEid, setToEid] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Don't offer routing a record to yourself.
  const options = people.filter((p) => p.employee_id !== employee?.employee_id);

  async function submit() {
    if (!toEid) {
      setError('Pick who should handle this.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await routeRecord({ entityType, entityId, routedToEid: toEid, reason });
      const name = options.find((o) => o.employee_id === toEid)?.name ?? toEid;
      toast.success(`Routed to ${name}`, {
        description: 'They’ll see it in their Inbox.',
      });
      setOpen(false);
      setToEid('');
      setReason('');
      onRouted?.();
    } catch (err) {
      setError(errorMessage(err, 'Failed to route this record.'));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <Button
        variant="secondary"
        size="sm"
        icon={<Send className="h-4 w-4" />}
        onClick={() => setOpen(true)}
      >
        Route to…
      </Button>
    );
  }

  return (
    <div className="rounded-md border border-tcs-border bg-tcs-bg p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-tcs-text">Route this record</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close"
          className="text-tcs-text-muted hover:text-tcs-text"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-2 space-y-2">
        <label className="block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
          Hand to
        </label>
        <select
          value={toEid}
          onChange={(e) => setToEid(e.target.value)}
          disabled={peopleLoading}
          className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
        >
          <option value="">{peopleLoading ? 'Loading…' : 'Select a person…'}</option>
          {options.map((o) => (
            <option key={o.employee_id} value={o.employee_id}>
              {o.name}
              {o.app_role ? ` · ${o.app_role}` : ''}
            </option>
          ))}
        </select>
        <label className="block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
          Note (why / what’s needed)
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="e.g. Special-pay hourly detail — please confirm AP setup."
          className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
        />
        {error && <p className="text-xs text-rag-red">{error}</p>}
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" loading={busy} onClick={submit}>
            Route
          </Button>
        </div>
      </div>
    </div>
  );
}
